import { useNetworkState } from '@uidotdev/usehooks';
import { useMemo } from 'react';
import { ModalContainer } from 'src/components/ModalContainer';
import type { DraftScheduleSendUnless } from 'src/pages/Conversations/Chat/conversation-draft-schedule';
import { useScheduleSendModalState } from 'src/pages/Conversations/Chat/InputField/hooks/use-schedule-send-modal-state';
import type { SendOptionItem } from 'src/pages/Conversations/Chat/InputField/send-options-button';
import { buildSendAndCommands } from 'src/pages/Conversations/Chat/InputField/smart-send/send-and-commands';
import { SmartSendConfirmStep } from 'src/pages/Conversations/Chat/InputField/smart-send/smart-send-confirm-step';
import { SmartSendConflictStep } from 'src/pages/Conversations/Chat/InputField/smart-send/smart-send-conflict-step';
import {
  type SmartSendConflict,
  SmartSendContext,
  type SmartSendContextValue,
} from 'src/pages/Conversations/Chat/InputField/smart-send/smart-send-context';
import { SmartSendTimeStep } from 'src/pages/Conversations/Chat/InputField/smart-send/smart-send-time-step';
import { useDisplayTime } from 'src/utils/hooks';

interface SmartSendModalProps {
  open: boolean;
  /** Set when a send is already scheduled: the flow starts on the edit-or-replace choice. */
  conflict?: SmartSendConflict;
  initialDate?: string | null;
  initialConditions?: DraftScheduleSendUnless[] | null;
  sendOptions: SendOptionItem[];
  onClose: () => void;
  onSubmit: (params: { scheduledAt: string; sendUnless: DraftScheduleSendUnless[] }) => void;
  onSelectOption: (optionId: string) => Promise<void> | void;
  isProcessingOption?: boolean;
}

/** The composer's Smart Send: pick when to send, confirm the cancel rules, schedule. */
export const SmartSendModal = ({
  open,
  conflict,
  initialDate,
  initialConditions,
  sendOptions,
  onClose,
  onSubmit,
  onSelectOption,
  isProcessingOption = false,
}: SmartSendModalProps) => {
  const schedule = useScheduleSendModalState({ open, initialDate, initialConditions });
  const { displayTime } = useDisplayTime();
  const { online } = useNetworkState();
  const { inputValue, onInputChange, buildSubmitPayload } = schedule;

  const context = useMemo<SmartSendContextValue>(
    () => ({
      schedule,
      sendAndCommands: buildSendAndCommands({
        sendOptions,
        inputValue,
        isRescheduling: Boolean(initialDate),
        displayTime,
      }),
      isProcessingOption,
      isOnline: online !== false,
      conflict,
      selectSendAnd: (optionId) => {
        void onSelectOption(optionId);
        onInputChange('');
        onClose();
      },
      save: () => {
        const payload = buildSubmitPayload();
        if (!payload) {
          return;
        }
        try {
          onSubmit(payload);
        } catch (error) {
          console.error(error);
        }
        onClose();
      },
      close: onClose,
    }),
    [
      schedule,
      sendOptions,
      inputValue,
      initialDate,
      displayTime,
      isProcessingOption,
      online,
      conflict,
      onSelectOption,
      onInputChange,
      onClose,
      buildSubmitPayload,
      onSubmit,
    ],
  );

  const renderFirstStep = () => {
    if (conflict) {
      return <SmartSendConflictStep conflict={conflict} />;
    }
    if (initialDate) {
      return <SmartSendConfirmStep canChangeTime />;
    }
    return <SmartSendTimeStep afterPick="confirm" />;
  };

  return (
    <SmartSendContext.Provider value={context}>
      <ModalContainer
        open={open}
        size="medium"
        onClose={onClose}
        data-testid="smart-send-modal"
      >
        {renderFirstStep()}
      </ModalContainer>
    </SmartSendContext.Provider>
  );
};
